import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

function Services() {
  const { category, letter } = useParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(category && category !== 'all' ? category : 'all');

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    const letterParam = urlParams.get('letter');
    
    // Determine the actual category from URL
    let actualCategory = 'all';
    let actualLetter = null;
    
    if (category) {
      // Check if category param is actually a letter (single character A-Z)
      if (category.length === 1 && /^[A-Z]$/i.test(category)) {
        // This is a letter, not a category - redirect to use query param instead
        navigate(`/services?letter=${category.toUpperCase()}`, { replace: true });
        return;
      } else if (category !== 'all') {
        // Valid category name
        actualCategory = category;
        setSelectedCategory(category);
      } else {
        actualCategory = 'all';
        setSelectedCategory('all');
      }
    } else {
      setSelectedCategory('all');
    }
    
    // Use letter from route param or query param
    if (letter) {
      actualLetter = letter.toUpperCase();
    } else if (letterParam) {
      actualLetter = letterParam.toUpperCase();
    }
    
    // Fetch services based on determined category and letter
    if (searchParam) {
      setSearchQuery(searchParam);
      fetchServices(actualCategory, actualLetter, searchParam);
    } else if (actualLetter) {
      fetchServices(actualCategory, actualLetter, null);
    } else {
      fetchServices(actualCategory, null, null);
    }
  }, [category, letter, navigate]);

  const fetchServices = async (cat, ltr, search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (cat && cat !== 'all') params.append('category', cat);
      if (ltr) params.append('letter', ltr);
      if (search) params.append('search', search);

      const response = await axios.get(`/api/services?${params}`);
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const currentCategory = category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || '')) ? category : selectedCategory;
      if (currentCategory === 'all') {
        navigate(`/services?search=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate(`/services/${currentCategory}?search=${encodeURIComponent(searchQuery)}`);
      }
      fetchServices(currentCategory, null, searchQuery);
      window.scrollTo(0, 0);
    }
  };

  const handleLetterClick = (ltr) => {
    const currentCategory = category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || '')) ? category : selectedCategory;
    if (currentCategory === 'all') {
      // Use query parameter when category is 'all' to avoid route conflict
      navigate(`/services?letter=${ltr}`);
    } else {
      navigate(`/services/${currentCategory}/${ltr}`);
    }
    setSearchQuery('');
    fetchServices(currentCategory, ltr, null);
    window.scrollTo(0, 0);
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    if (cat === 'all') {
      navigate('/services');
    } else {
      navigate(`/services/${cat}`);
    }
    setSearchQuery('');
    window.scrollTo(0, 0);
    fetchServices(cat, null, null);
  };

  // Group services by first letter for display
  const groupServices = (serviceList) => {
    const grouped = {};
    serviceList.forEach(service => {
      const key = service.first_letter;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(service);
    });
    return grouped;
  };

  const groupedServices = groupServices(services);

  const categoryLabels = {
    all: 'All Services',
    general_care: 'General Care',
    diagnostic: 'Diagnostic',
    specialized: 'Specialized'
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-4" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none" style={{ color: '#0066cc' }}>
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Services
              </li>
            </ol>
          </nav>
          <h1 className="display-4 mb-3" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', color: '#003d82' }}>
            {category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || '')) ? categoryLabels[category] || 'Our Services' : 'Our Services'}
          </h1>
          <p className="lead mb-0" style={{ fontSize: '1.1rem', color: '#666' }}>
            {category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || ''))
              ? `Comprehensive ${categoryLabels[category]?.toLowerCase() || 'services'} for you and your family`
              : 'Comprehensive healthcare services for you and your family'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-4" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row">
            {/* Left Sidebar - Filters */}
            <div className="col-lg-3 mb-4 mb-lg-0">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-3" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    Narrow your search
                  </h5>
                  <div className="mb-4">
                    <h6 className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600' }}>By category</h6>
                    <div className="list-group list-group-flush">
                      {(() => {
                        // Determine active category from URL or state
                        const activeCategory = (category && category !== 'all' && !(/^[A-Z]$/i.test(category || ''))) ? category : selectedCategory;
                        const isAllActive = !category || category === 'all' || selectedCategory === 'all';
                        const isGeneralCareActive = activeCategory === 'general_care';
                        const isDiagnosticActive = activeCategory === 'diagnostic';
                        const isSpecializedActive = activeCategory === 'specialized';
                        
                        return (
                          <>
                            <button
                              className={`list-group-item list-group-item-action border-0 px-0 ${isAllActive ? 'active' : ''}`}
                              onClick={() => handleCategoryChange('all')}
                              style={{ 
                                cursor: 'pointer', 
                                backgroundColor: isAllActive ? '#e7f3ff' : 'transparent',
                                color: isAllActive ? '#0066cc' : '#333',
                                fontWeight: isAllActive ? '600' : '400',
                                borderLeft: isAllActive ? '3px solid #0066cc' : '3px solid transparent',
                                paddingLeft: isAllActive ? 'calc(1rem - 3px)' : '1rem',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              All
                            </button>
                            <button
                              className={`list-group-item list-group-item-action border-0 px-0 ${isGeneralCareActive ? 'active' : ''}`}
                              onClick={() => handleCategoryChange('general_care')}
                              style={{ 
                                cursor: 'pointer', 
                                backgroundColor: isGeneralCareActive ? '#e7f3ff' : 'transparent',
                                color: isGeneralCareActive ? '#0066cc' : '#333',
                                fontWeight: isGeneralCareActive ? '600' : '400',
                                borderLeft: isGeneralCareActive ? '3px solid #0066cc' : '3px solid transparent',
                                paddingLeft: isGeneralCareActive ? 'calc(1rem - 3px)' : '1rem',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              General Care
                            </button>
                            <button
                              className={`list-group-item list-group-item-action border-0 px-0 ${isDiagnosticActive ? 'active' : ''}`}
                              onClick={() => handleCategoryChange('diagnostic')}
                              style={{ 
                                cursor: 'pointer', 
                                backgroundColor: isDiagnosticActive ? '#e7f3ff' : 'transparent',
                                color: isDiagnosticActive ? '#0066cc' : '#333',
                                fontWeight: isDiagnosticActive ? '600' : '400',
                                borderLeft: isDiagnosticActive ? '3px solid #0066cc' : '3px solid transparent',
                                paddingLeft: isDiagnosticActive ? 'calc(1rem - 3px)' : '1rem',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Diagnostic
                            </button>
                            <button
                              className={`list-group-item list-group-item-action border-0 px-0 ${isSpecializedActive ? 'active' : ''}`}
                              onClick={() => handleCategoryChange('specialized')}
                              style={{ 
                                cursor: 'pointer', 
                                backgroundColor: isSpecializedActive ? '#e7f3ff' : 'transparent',
                                color: isSpecializedActive ? '#0066cc' : '#333',
                                fontWeight: isSpecializedActive ? '600' : '400',
                                borderLeft: isSpecializedActive ? '3px solid #0066cc' : '3px solid transparent',
                                paddingLeft: isSpecializedActive ? 'calc(1rem - 3px)' : '1rem',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Specialized
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Center Content - Services List */}
            <div className="col-lg-6">
              {/* Search Bar */}
              <div className="mb-4">
                <form onSubmit={handleSearch}>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search services..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn btn-primary" type="submit">
                      <i className="bi bi-search"></i>
                    </button>
                  </div>
                </form>
              </div>

              {/* Letter Filter */}
              <div className="mb-4">
                <p className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600' }}>
                  Find {(category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || '')) ? categoryLabels[category] : categoryLabels[selectedCategory]) || 'services'} by first letter
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {alphabet.map((ltr) => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const letterParam = urlParams.get('letter');
                    // Check if letter is active from URL param or route param
                    const isActive = letter === ltr || letterParam === ltr;
                    return (
                      <button
                        key={ltr}
                        type="button"
                        className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleLetterClick(ltr)}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          fontWeight: '600',
                          border: '2px solid #003d82',
                          color: isActive ? '#fff' : '#003d82',
                          backgroundColor: isActive ? '#003d82' : 'transparent'
                        }}
                      >
                        {ltr}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Services List */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="mb-4" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {(category && category !== 'all' && !(/^[A-Z]$/.test(category?.toUpperCase() || '')) ? categoryLabels[category] : categoryLabels[selectedCategory]) || 'All Services'}
                  </h2>
                  {services.length === 0 ? (
                    <p className="text-muted">No services found.</p>
                  ) : (
                    Object.keys(groupedServices).sort().map((key) => (
                      <div key={key} className="mb-5">
                        <h3 className="mb-3" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1a1a1a' }}>
                          {key}
                        </h3>
                        <ul className="list-unstyled">
                          {groupedServices[key].map((service) => (
                            <li key={service.id} className="mb-3">
                              <div className="d-flex align-items-start">
                                {service.image && (
                                  <img
                                    src={service.image}
                                    alt={service.name}
                                    className="me-3"
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div>
                                  {service.alias ? (
                                    <>
                                      <span className="d-block mb-1" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                                        {service.alias}
                                      </span>
                                      <Link
                                        to={`/services/detail/${service.id}`}
                                        className="text-decoration-none"
                                        style={{ color: '#0066cc', fontSize: '0.95rem' }}
                                      >
                                        See {service.name}
                                      </Link>
                                    </>
                                  ) : (
                                    <Link
                                      to={`/services/detail/${service.id}`}
                                      className="text-decoration-none"
                                      style={{ color: '#0066cc', fontSize: '1.1rem', fontWeight: '600' }}
                                    >
                                      {service.name}
                                    </Link>
                                  )}
                                  {service.description && (
                                    <p className="text-muted mb-1 mt-1" style={{ fontSize: '0.9rem' }}>
                                      {service.description.substring(0, 150)}
                                      {service.description.length > 150 ? '...' : ''}
                                    </p>
                                  )}
                                  {(service.operating_days || service.operating_hours) && (
                                    <div className="mt-2">
                                      {service.operating_days && (
                                        <p className="mb-1" style={{ fontSize: '0.85rem', color: '#666' }}>
                                          <i className="bi bi-calendar3 me-1"></i>
                                          <strong>Days:</strong> {service.operating_days}
                                        </p>
                                      )}
                                      {service.operating_hours && (
                                        <p className="mb-0" style={{ fontSize: '0.85rem', color: '#666' }}>
                                          <i className="bi bi-clock me-1"></i>
                                          <strong>Hours:</strong> {service.operating_hours}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <a
                          href="#top"
                          onClick={(e) => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ color: '#0066cc', fontSize: '0.9rem', textDecoration: 'none', cursor: 'pointer' }}
                        >
                          Back to top <i className="bi bi-arrow-up"></i>
                        </a>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>

            {/* Right Sidebar - Appointments */}
            <div className="col-lg-3">
              <div className="card border-0" style={{ backgroundColor: '#e8f4f8', borderRadius: '8px' }}>
                <div className="card-body">
                  <h5 className="card-title mb-3" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    Appointments
                  </h5>
                  <p className="card-text mb-3" style={{ fontSize: '0.9rem', color: '#333' }}>
                    Tema General Hospital accepts appointments for all our services. Schedule your visit with our specialists.
                  </p>
                  <Link to="/contact" className="btn btn-primary btn-sm">
                    Contact us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;
